/**
 * Publish readiness model (W9) — pure, presentation-free reasoning over the
 * `publish-preview` response + the plan status, so both the screen and its
 * tests agree on the gate without touching the DOM.
 *
 * The gate MIRRORS the backend: a plan may publish only when it is *approved or
 * locked* AND validation has no blocking (error-level) issue. A draft can never
 * publish — even if every check passes — because the plan-of-record must clear
 * the approval engine (D18/D24) first. `publish-preview` reports `ready` from
 * the validation side only; the status side is enforced here and server-side.
 */
import type { PlanStatus, PublishPreviewOut, ValidationIssue } from "@api/types";
import { asArray } from "@lib/asArray";

/** Plan states the backend accepts a publish from. */
const PUBLISHABLE_STATUSES: ReadonlySet<PlanStatus> = new Set<PlanStatus>([
  "approved",
  "locked",
]);

export interface CheckCounts {
  ok: number;
  warn: number;
  error: number;
}

/**
 * Count validation issues by severity. The list of checks the backend ran is
 * *implicit* — `publish-preview` returns only the ones that produced a
 * finding, plus `ready`. We surface warn/error counts; OK is the passing
 * remainder the caller supplies (see `checkCounts`).
 */
export function issueCounts(issues: unknown): { warn: number; error: number } {
  let warn = 0;
  let error = 0;
  for (const i of asArray<ValidationIssue>(issues)) {
    if (i?.level === "error") error += 1;
    else if (i?.level === "warn") warn += 1;
  }
  return { warn, error };
}

/**
 * The set of validation checks the plan is graded against. The backend runs
 * these server-side; the preview reports only the ones that flagged. We list
 * them here so the UI can render a full pass/fail checklist (a passing check =
 * one with no matching issue code), matching the w9 wireframe.
 */
export const CHECK_CODES = [
  "required_fields",
  "tvd_le_md",
  "monotonic_depth",
  "cost_balance",
  "casing_nesting",
  "program_sequence",
] as const;
export type CheckCode = (typeof CHECK_CODES)[number];

export interface CheckRow {
  code: CheckCode;
  /** Worst severity found for this check; "good" when nothing flagged. */
  level: "good" | "warn" | "error";
  /** The backend message for the flagged case, if any. */
  message: string | null;
}

/**
 * Fold the preview issues into one row per known check, so the screen renders a
 * stable, ordered checklist regardless of how many issues the backend returned.
 * An issue whose `code` is not a known check still counts toward readiness (via
 * `issueCounts`) but does not collapse a named row — it is surfaced separately.
 */
export function checkRows(issues: unknown): CheckRow[] {
  const byCode = new Map<string, ValidationIssue>();
  for (const i of asArray<ValidationIssue>(issues)) {
    if (!i?.code) continue;
    const prior = byCode.get(i.code);
    // error outranks warn for the collapsed row severity.
    if (!prior || (prior.level !== "error" && i.level === "error")) byCode.set(i.code, i);
  }
  return CHECK_CODES.map((code) => {
    const hit = byCode.get(code);
    return {
      code,
      level: hit ? hit.level : "good",
      message: hit?.message ?? null,
    };
  });
}

/** OK / warn / error counts across the full named-check list. */
export function checkCounts(issues: unknown): CheckCounts {
  const rows = checkRows(issues);
  let ok = 0;
  let warn = 0;
  let error = 0;
  for (const r of rows) {
    if (r.level === "error") error += 1;
    else if (r.level === "warn") warn += 1;
    else ok += 1;
  }
  return { ok, warn, error };
}

/** Issues whose code is not one of the named checks (surfaced separately). */
export function extraIssues(issues: unknown): ValidationIssue[] {
  const known = new Set<string>(CHECK_CODES);
  return asArray<ValidationIssue>(issues).filter((i) => i?.code && !known.has(i.code));
}

export interface PublishGate {
  /** Validation passed (no error-level issue in the preview). */
  validationReady: boolean;
  /** Plan status permits a publish (approved / locked). */
  statusReady: boolean;
  /** Both gates clear — the publish button may enable. */
  canPublish: boolean;
}

/**
 * Resolve the publish gate from the preview + the plan status. `preview` may be
 * undefined (still loading / errored) — then nothing is publishable. We trust
 * the server's `ready` for validation but also recompute error count from the
 * issues so a malformed `ready:true, issues:[error]` never opens the gate.
 */
export function resolveGate(
  preview: PublishPreviewOut | undefined,
  status: PlanStatus | undefined,
): PublishGate {
  const { error } = issueCounts(preview?.issues);
  const validationReady = Boolean(preview) && preview!.ready === true && error === 0;
  const statusReady = status != null && PUBLISHABLE_STATUSES.has(status);
  return {
    validationReady,
    statusReady,
    canPublish: validationReady && statusReady,
  };
}
